import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import useService from '@/lib/hooks/useService';
import type { RootState } from '@/redux/store';
import type Announcement from '@/types/database/announcement';

import {
  freezeStatusOptions,
  getColumns,
  getMemberFreezeList,
} from '../helper';
import type { MemberFreezeResponse, MemberFreezeRule } from '../types';
import styles from './index.module.scss';

const { Title } = Typography;
const { Item, useForm } = Form;

const FreezeManagement = () => {
  const [form] = useForm();

  const [memberFreezeRuleField, , communityDataService] = useService<{
    _id: string;
    memberFreezeRule: MemberFreezeRule;
  }>({
    collectionName: 'communityData',
    query: { $select: ['memberFreezeRule'] },
  });
  const [, , announcementService] = useService<Announcement>({
    collectionName: 'announcement',
  });

  const userData = useSelector((state: RootState) => state.user);
  const announcer = userData?.auth?.user?.name;

  const [isEdit, setIsEdit] = useState(false);
  const [tableDataSource, setTableDataSource] = useState<
    MemberFreezeResponse[]
  >([]);
  const [inputPhoneNumber, setInputPhoneNumber] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  const getTableData = async () => {
    try {
      const res = await axios({
        method: 'post',
        url: '/ezcon/api/memberFreezeManagementList',
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
      });
      const data = res.data;
      setTableDataSource(data);
    } catch (error) {
      message.error('會員帳號資料載入失敗!');
    }
  };

  useEffect(() => {
    if (memberFreezeRuleField[0]) {
      const memberFreezeRule = memberFreezeRuleField[0].memberFreezeRule;
      const freezeRule = {
        ...memberFreezeRule,
        freezeDurationLimit: dayjs(memberFreezeRule.freezeDurationLimit).diff(
          dayjs(0),
          'day'
        ),
      };
      form.setFieldsValue(freezeRule);
    }
  }, [form, memberFreezeRuleField]);

  useEffect(() => {
    getTableData();
  }, []);

  const handleAnnouncementFormEdit = async (formValues: MemberFreezeRule) => {
    const { maximumFreezeCount, freezeDurationLimit } = formValues;

    try {
      await communityDataService.patch(memberFreezeRuleField[0]._id, {
        memberFreezeRule: {
          maximumFreezeCount,
          freezeDurationLimit: dayjs(0)
            .add(freezeDurationLimit, 'day')
            .valueOf(),
        },
      });
      setIsEdit(false);
      message.success('帳號凍結管理辦法變更成功!');
    } catch (error) {
      message.error('帳號凍結管理辦法變更失敗!');
    }
  };

  const publishAnnouncement = async () => {
    const { maximumFreezeCount, freezeDurationLimit } = form.getFieldsValue();
    const data = {
      title: '更新帳號凍結管理辦法',
      announcementTime: dayjs().valueOf(),
      content: `1.單一會員帳號可凍結次數最多${maximumFreezeCount}次。2.單一會員帳號累積凍結時間上限${freezeDurationLimit}天。`,
      type: '一般',
      fileType: 'text',
      files: [],
      allows: [],
      broadcastStatus: false,
      broadcastGroupId: null,
      calendarStatus: false,
      calendarId: null,
      MessageStatus: false,
      checkedStatus: false,
      announcer,
      endTime: null,
    };

    try {
      await announcementService.create(data);
      message.success('發佈公告成功!');
    } catch (error) {
      message.error('發佈公告失敗!');
    }
  };

  const columns = getColumns(getTableData);
  const memberFreezeList = getMemberFreezeList(
    tableDataSource,
    inputPhoneNumber,
    selectedStatus
  );

  return (
    <Space direction="vertical" className={styles.widthFull}>
      <Space size={20}>
        <Space size={0}>
          <span>會員：</span>
          <Input
            placeholder="請輸入會員手機號碼"
            allowClear
            onChange={(event) => setInputPhoneNumber(event.target.value)}
          />
        </Space>
        <Space size={0}>
          <span>狀態：</span>
          <Select
            allowClear
            placeholder="請選擇狀態"
            className={styles.selector}
            onChange={(value) => setSelectedStatus(value)}
            options={freezeStatusOptions}
          />
        </Space>
      </Space>
      <Space direction="vertical" className={styles.widthFull} size="large">
        <Card>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleAnnouncementFormEdit}
          >
            <Space direction="vertical" className={styles.widthFull} size={5}>
              <Title level={5}>帳號凍結管理辦法</Title>
              <Space align="baseline">
                <span>1.單一會員帳號可凍結次數最多</span>
                <Item
                  name="maximumFreezeCount"
                  className={styles.noMarginBottom}
                >
                  <InputNumber min={0} disabled={!isEdit} />
                </Item>
                <span>次</span>
              </Space>
              <Space align="baseline">
                <span>2.單一會員帳號累積凍結時間上限</span>
                <Item
                  name="freezeDurationLimit"
                  className={styles.noMarginBottom}
                >
                  <InputNumber min={0} disabled={!isEdit} />
                </Item>
                <span>天</span>
              </Space>
              <Item>
                <Space className={styles.formFooterButtonsContainer}>
                  <Button
                    type={isEdit ? 'default' : 'primary'}
                    onClick={() =>
                      isEdit ? setIsEdit(false) : publishAnnouncement()
                    }
                  >
                    {isEdit ? '取消' : '發佈公告'}
                  </Button>
                  {
                    <Button
                      type={isEdit ? 'primary' : 'default'}
                      onClick={() => (isEdit ? form.submit() : setIsEdit(true))}
                    >
                      {isEdit ? '儲存' : '編輯'}
                    </Button>
                  }
                </Space>
              </Item>
            </Space>
          </Form>
        </Card>
        <Card>
          <Title level={5}>會員帳號資料</Title>
          <Table
            bordered
            rowKey={(record) => record._id + record.subscriptionPlanId}
            scroll={{ x: 'max-content' }}
            columns={columns}
            dataSource={memberFreezeList}
          />
        </Card>
      </Space>
    </Space>
  );
};

export default FreezeManagement;
